import React, { useState, useEffect } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import AppButton from "../Common/Button/Button";
import CKEditor from "ckeditor4-react";
import { sendEmail } from "../../action/report";
import { connect } from "react-redux";
import { parseEmailList } from "../../utils/general";
import { SectionHeading, LabelText, ValueText } from "../Common/Typography/AppTypography";

const SendingEmail = ({
  deviceInfo,
  emailSendingResponse,
  sendEmail,
  loggedInUserName,
  handleEmailSendingPopupClose
}) => {
  const [emailContent, setEmailContent] = useState("");
  const [showSentEmailMessage, setShowSentEmailMessage] = useState(false);

  const userName = loggedInUserName;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (!emailSendingResponse) return; // skip initial null
    if (emailSendingResponse.message && emailSendingResponse.message==="Success" && showSentEmailMessage) {
      setTimeout(()=>{
        if (typeof handleEmailSendingPopupClose === "function") {
          handleEmailSendingPopupClose();
        }
      }, 800)
    }
  }, [emailSendingResponse, handleEmailSendingPopupClose, showSentEmailMessage]);

  const onEditorChange = (evt) => {
    setEmailContent(evt.editor.getData());
  };

  const sendIncidentEmail = () => {
    const emailBody = `</br><b>Device Id:</b>${deviceInfo.deviceId},</br><b>Category:</b>${deviceInfo.category},</br><b>Model Number:</b>${deviceInfo.modelNumber},</br><b>Firmware Version:</b>${deviceInfo.firmwareVersion},</br><b>Brand:</b>${deviceInfo.brand},</br><b>Onboarded By:</b>: ${deviceInfo.onboardedBy},${emailContent}`;

    let receiptList = process.env.REACT_APP_EMAIL_SENDING_RECEIPT_LIST;
    if(receiptList){
      receiptList = parseEmailList(receiptList);

      sendEmail(emailBody, "Miraie support incident", receiptList, userName);
      setShowSentEmailMessage(true);
    }
    else{
      console.error("email list is not valid---", receiptList)
    }
  };

  const valueSx = { wordBreak: "break-word", overflowWrap: "break-word", minWidth: 0 };

  return (
    <div className="email-sending-popup" style={{ paddingBottom: isMobile ? 40 : 0 }}>
      <Box
        sx={{
          bgcolor: "#f3f3f3",
          borderRadius: 1,
          p: { xs: 1, sm: 2 },
          mb: { xs: 1.5, sm: 2 },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(3, minmax(0, 1fr))",
              sm: "repeat(3, auto minmax(0, 1fr))",
            },
            columnGap: { xs: 0.75, sm: 1.5 },
            rowGap: { xs: 0.5, sm: 1.5 },
          }}
        >
          {isMobile ? (
            <>
              <Box sx={{ minWidth: 0 }}>
                <LabelText sx={{ fontSize: "0.75rem", display: "block" }}>Device ID</LabelText>
                <ValueText sx={{ ...valueSx, fontSize: "0.75rem" }}>{deviceInfo?.deviceId || "-"}</ValueText>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <LabelText sx={{ fontSize: "0.75rem", display: "block" }}>Model No.</LabelText>
                <ValueText sx={{ ...valueSx, fontSize: "0.75rem" }}>{deviceInfo?.modelNumber || "-"}</ValueText>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <LabelText sx={{ fontSize: "0.75rem", display: "block" }}>Brand</LabelText>
                <ValueText sx={{ ...valueSx, fontSize: "0.75rem" }}>{deviceInfo?.brand || "-"}</ValueText>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <LabelText sx={{ fontSize: "0.75rem", display: "block" }}>Category</LabelText>
                <ValueText sx={{ ...valueSx, fontSize: "0.75rem" }}>{deviceInfo?.category || "-"}</ValueText>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <LabelText sx={{ fontSize: "0.75rem", display: "block" }}>Firmware</LabelText>
                <ValueText sx={{ ...valueSx, fontSize: "0.75rem" }}>{deviceInfo?.firmwareVersion || "-"}</ValueText>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <LabelText sx={{ fontSize: "0.75rem", display: "block" }}>Onboarded By</LabelText>
                <ValueText sx={{ ...valueSx, fontSize: "0.75rem" }}>{deviceInfo?.onboardedBy || "-"}</ValueText>
              </Box>
            </>
          ) : (
            <>
              <LabelText>Device ID:</LabelText>
              <ValueText sx={valueSx}>{deviceInfo?.deviceId || "-"}</ValueText>
              <LabelText>Model Number:</LabelText>
              <ValueText sx={valueSx}>{deviceInfo?.modelNumber || "-"}</ValueText>
              <LabelText>Brand Name:</LabelText>
              <ValueText sx={valueSx}>{deviceInfo?.brand || "-"}</ValueText>
              <LabelText>Category:</LabelText>
              <ValueText sx={valueSx}>{deviceInfo?.category || "-"}</ValueText>
              <LabelText>Current Firmware:</LabelText>
              <ValueText sx={valueSx}>{deviceInfo?.firmwareVersion || "-"}</ValueText>
              <LabelText>Onboarded By:</LabelText>
              <ValueText sx={valueSx}>{deviceInfo?.onboardedBy || "-"}</ValueText>
            </>
          )}
        </Box>
      </Box>
      <Box sx={{ px: { xs: 0, sm: 2.5 } }}>
        <SectionHeading sx={{ color: "text.primary", mb: { xs: 0.5, sm: 1 }, fontSize: { xs: "0.875rem", sm: "inherit" } }}>Incidence Details</SectionHeading>

        <Box
          sx={{
            "& .cke_contents": {
              minHeight: { xs: 100, sm: 200 },
              maxHeight: { xs: 140, sm: "none" },
            },
            "& .cke_toolbar": { flexWrap: "wrap" },
          }}
        >
          <CKEditor
          onChange={onEditorChange}
          config={{
            versionCheck: false,
            toolbar: isMobile
              ? [
                  { name: "clipboard", items: ["Cut", "Copy", "Paste", "-", "Undo", "Redo"] },
                  { name: "basicstyles", items: ["Bold", "Italic", "Strike"] },
                  { name: "paragraph", items: ["NumberedList", "BulletedList", "-", "Blockquote"] },
                  { name: "links", items: ["Link", "Unlink"] },
                  { name: "document", items: ["Source"] },
                ]
              : [
                  { name: "clipboard", items: ["Cut", "Copy", "-", "Undo", "Redo"] },
                  { name: "editing", groups: ["Heading", "Find", "Selection", "SpellChecker", "Editing"] },
                  { name: "basicstyles", items: ["Bold", "Italic", "Underline", "Subscript", "Superscript", "Strike", "-", "RemoveFormate"] },
                  { name: "paragraph", items: ["NumberedList", "BulletedList", "-", "Outdent", "Indent", "Blockquote", "List", "Blocks", "Align", "Bidi", "Paragraph"] },
                  { name: "styles", items: ["Styles", "Format"] },
                  "/",
                  { name: "links", items: ["Link", "Unlink", "Anchor"] },
                  { name: "insert", items: ["Table", "mergeTableCells", "MergeTableCells", "HorizontalRule", "SpecialChar"] },
                  { name: "document", items: ["Source", "-", "NewPage", "Preview", "-", "Templates"] },
                  { name: "colors", groups: ["Colors"] },
                  { name: "tools", groups: ["Tools"] },
                  { name: "others", groups: ["Others"] },
                  "/",
                  { name: "forms", groups: ["Forms"] },
                  { name: "document", groups: ["Mode", "Document", "Doctools"] },
                ],
          }}
        />
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          flexWrap: "wrap",
          justifyContent: { xs: "flex-start", sm: "space-between" },
          alignItems: { xs: "stretch", sm: "center" },
          gap: 1.5,
          pt: { xs: 1.5, sm: 2 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LabelText>Sent By:</LabelText>
          <ValueText>{userName}</ValueText>
        </Box>

        {showSentEmailMessage && (
          <ValueText sx={{ color: "success.main" }}>
            {emailSendingResponse.message}
          </ValueText>
        )}
        <AppButton
          variant="primary"
          size="medium"
          onClick={sendIncidentEmail}
          sx={{ minWidth: { xs: "100%", sm: 140 }, alignSelf: { xs: "stretch", sm: "auto" } }}
        >
          Send E-mail
        </AppButton>
      </Box>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    loggedInUserName: state.auth.username,
    emailSendingResponse: state.report.emailSendingResponse,
  };
};

export default connect(mapStateToProps, { sendEmail })(SendingEmail);
